import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cemeteryAPI, importAPI } from '../services/api';
import './WipePage.css';
import Toast from '../components/Toast';
import CemeteryStatsGrid from '../components/CemeteryStatsGrid';
import CemeteryContextBar from '../components/CemeteryContextBar';

const WIPE_TYPE_LABELS = {
    plots: 'Plots',
    geojson: 'Plot GeoJSON',
    sections: 'Section GeoJSON',
    lots: 'Lot GeoJSON',
    persons: 'Persons',
    interments: 'Interments',
    applications: 'Applications',
    stories: 'Stories',
    events: 'Events',
    notes: 'Notes',
};

function WipePage() {
    const [searchParams] = useSearchParams();
    const [cemeteries, setCemeteries] = useState([]);
    const [selectedCemetery, setSelectedCemetery] = useState(searchParams.get('cemetery') || '');
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [statistics, setStatistics] = useState(null);
    const [wipeTypes, setWipeTypes] = useState({
        plots: false,
        geojson: false,
        sections: false,
        lots: false,
        persons: false,
        interments: false,
        applications: false,
        stories: false,
        events: false,
        notes: false,
    });

    useEffect(() => {
        loadCemeteries();
    }, []);

    useEffect(() => {
        if (selectedCemetery) {
            loadStatistics();
        } else {
            setStatistics(null);
        }
    }, [selectedCemetery]);

    const loadStatistics = async ({ refresh = false } = {}) => {
        if (!selectedCemetery) return;
        try {
            const response = await importAPI.getStatistics(selectedCemetery, { refresh });
            setStatistics(response.data);
        } catch (err) {
            console.error('Failed to load statistics:', err);
            setStatistics(null);
        }
    };

    const loadCemeteries = async () => {
        try {
            const response = await cemeteryAPI.list();
            setCemeteries(response.data.cemeteries || response.data);
        } catch (err) {
            console.error('Failed to load cemeteries:', err);
        }
    };

    const filteredCemeteries = cemeteries.filter(cemetery =>
        cemetery.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cemetery.unique_name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleSelectCemetery = (cemeteryUniqueName) => {
        setSelectedCemetery(cemeteryUniqueName);
        setSearchQuery('');
    };

    const handleWipeTypeChange = (type) => {
        setWipeTypes({
            ...wipeTypes,
            [type]: !wipeTypes[type],
        });
    };

    const handleSelectAll = () => {
        const allSelected = Object.values(wipeTypes).every(Boolean);
        const newWipeTypes = {};
        Object.keys(wipeTypes).forEach(key => {
            newWipeTypes[key] = !allSelected;
        });
        setWipeTypes(newWipeTypes);
    };

    const handleWipeData = async () => {
        const selectedTypes = Object.keys(wipeTypes).filter((key) => wipeTypes[key]);
        if (selectedTypes.length === 0) {
            setMessage({ type: 'error', text: 'Please select at least one data type to wipe' });
            return;
        }

        const typesText = selectedTypes.join(', ');
        if (!window.confirm(
            `DANGER: This will permanently delete all ${typesText} data from ${selectedCemetery}. ` +
            `This action cannot be undone. Are you absolutely sure?`
        )) {
            return;
        }

        setLoading(true);
        setMessage(null);

        try {
            const response = await importAPI.wipeData(selectedCemetery, selectedTypes);
            setMessage({
                type: 'success',
                text: `Successfully wiped ${response.data.total_deleted} records (${typesText})`,
            });
            loadStatistics();
            // Reset wipe checkboxes
            setWipeTypes({
                plots: false,
                geojson: false,
                sections: false,
                lots: false,
                persons: false,
                interments: false,
                applications: false,
                stories: false,
                events: false,
                notes: false,
            });
        } catch (err) {
            setMessage({
                type: 'error',
                text: err.response?.data?.detail || 'Failed to wipe data',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container">
            <CemeteryContextBar
                cemeteryUniqueName={selectedCemetery}
                cemeteryName={cemeteries.find(c => c.unique_name === selectedCemetery)?.name}
                onChange={() => setSelectedCemetery('')}
            />
            <div className="page-header">
                <div className="page-header-text">
                    <h1>Wipe Data</h1>
                    <p>Permanently remove cemetery data records</p>
                </div>
                <div className="page-header-actions">
                    <button
                        className="btn btn-danger"
                        onClick={handleWipeData}
                        disabled={!selectedCemetery || !Object.values(wipeTypes).some(Boolean) || loading}
                    >
                        Wipe Selected Data
                    </button>
                </div>
            </div>

            <Toast message={message} onDismiss={() => setMessage(null)} />

            {/* Cemetery Selection */}
            <div className="card">
                <h2>Select Cemetery</h2>
                <div className="input-group">
                    <label htmlFor="cemetery">Cemetery</label>
                    <div className="search-container">
                        <input
                            type="text"
                            id="cemetery"
                            className="form-control"
                            placeholder={selectedCemetery ?
                                cemeteries.find(c => c.unique_name === selectedCemetery)?.name || selectedCemetery
                                : "Search cemeteries..."}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            disabled={loading}
                        />
                        {searchQuery && filteredCemeteries.length > 0 && (
                            <div className="search-results">
                                {filteredCemeteries.map((cemetery) => (
                                    <div
                                        key={cemetery.unique_name}
                                        className="search-result-item"
                                        onClick={() => handleSelectCemetery(cemetery.unique_name)}
                                    >
                                        <strong>{cemetery.name}</strong>
                                        <p className="text-muted">{cemetery.unique_name}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                        {searchQuery && filteredCemeteries.length === 0 && (
                            <div className="search-results">
                                <p className="text-muted">No cemeteries found</p>
                            </div>
                        )}
                    </div>
                    {selectedCemetery && (
                        <div className="selected-item">
                            <strong>Selected:</strong> {cemeteries.find(c => c.unique_name === selectedCemetery)?.name || selectedCemetery}
                            <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => setSelectedCemetery('')}
                                disabled={loading}
                            >
                                Change
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Cemetery Statistics */}
            {selectedCemetery && statistics && (
                <CemeteryStatsGrid statistics={statistics} title="Cemetery Statistics" onRefresh={() => loadStatistics({ refresh: true })} />
            )}

            {/* Wipe Data Section */}
            {selectedCemetery && (
                <div className="card" style={{ borderColor: '#d32f2f', borderWidth: '2px' }}>
                    <h2 style={{ color: '#d32f2f' }}>⚠️ Wipe Cemetery Data</h2>
                    <p className="text-muted" style={{ color: '#d32f2f', fontWeight: 'bold' }}>
                        DANGER: This will permanently delete selected data from the database. This action cannot be undone!
                    </p>

                    <div className="import-types">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <label style={{ marginBottom: 0 }}>Select Data Types to Wipe:</label>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={handleSelectAll}
                                disabled={loading}
                            >
                                {Object.values(wipeTypes).every(Boolean) ? 'Deselect All' : 'Select All'}
                            </button>
                        </div>
                        <div className="checkbox-grid">
                            {Object.keys(wipeTypes).map((type) => (
                                <label key={type} className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={wipeTypes[type]}
                                        onChange={() => handleWipeTypeChange(type)}
                                        disabled={loading}
                                    />
                                    <span>{WIPE_TYPE_LABELS[type] || (type.charAt(0).toUpperCase() + type.slice(1))}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <button
                        onClick={handleWipeData}
                        className="btn btn-danger"
                        disabled={loading}
                        style={{ marginTop: '10px' }}
                    >
                        {loading ? 'Wiping Data...' : 'Wipe Selected Data'}
                    </button>
                </div>
            )}
        </div>
    );
}

export default WipePage;
